// Availability forecast strip — a compact area chart of available drones per day,
// aligned 1:1 with the grid's day columns and living inside the same scroll
// container so it pans horizontally in lockstep. Fed by the SAME backend forecast
// the dashboard uses (anchored at the window start), refreshed on every mutation,
// so consolidating shows into a tour visibly lifts the line in real time.

import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import { formatEventDate } from '../utils/date-utils';
import { ForecastPoint } from './types';
import { DAY_W, INFO_W } from './grid-constants';

interface Props {
  days: string[];
  todayIdx?: number;
  totalWidth: number;
}

const H = 64;          // plot height
const PAD_T = 8;       // top padding inside the plot
const PAD_B = 6;       // bottom padding

const ForecastStrip: React.FC<Props> = ({ days, todayIdx, totalWidth }) => {
  const forecast = useVehicleScheduleStore(s => s.forecast);
  const forecastError = useVehicleScheduleStore(s => s.forecastError);
  const dayMode = useThemeStore(s => s.dayMode);
  // Distinguish "couldn't compute" from a genuinely empty series — an errored
  // strip must never read as healthy all-clear availability.
  const unavailable = forecastError || forecast.length === 0;

  const byDate = useMemo(() => {
    const m = new Map<string, number>();
    forecast.forEach(p => m.set(p.date, p.availableDrones));
    return m;
  }, [forecast]);
  const pointByDate = useMemo(() => {
    const m = new Map<string, ForecastPoint>();
    forecast.forEach(p => m.set(p.date, p));
    return m;
  }, [forecast]);

  const plotRef = useRef<HTMLDivElement>(null);
  // `top` = the strip's viewport top, so the tooltip floats just ABOVE the row
  // (offset from the cursor) instead of following it.
  const [hover, setHover] = useState<{ idx: number; x: number; top: number } | null>(null);
  const onMove = (ev: React.MouseEvent) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect) return;
    const idx = Math.floor((ev.clientX - rect.left) / DAY_W);
    if (idx < 0 || idx >= days.length) { setHover(null); return; }
    setHover({ idx, x: ev.clientX, top: rect.top });
  };
  const onLeave = () => setHover(null);

  // Per-day available drones, aligned to the visible day columns.
  const vals = useMemo(() => days.map(d => (byDate.has(d) ? byDate.get(d)! : null)), [days, byDate]);
  const present = vals.filter((v): v is number => v != null);

  const totalFleet = forecast[0]?.totalFleet ?? 0;
  const minAvail = present.length ? Math.min(...present) : null;

  // Scale: 0 at the bottom (or below if overbooked), the post-lease ceiling at top.
  const ceiling = useMemo(() => {
    const maxAvailFleet = Math.max(0, ...forecast.map(p => p.availableFleet));
    return Math.max(1, maxAvailFleet, ...present);
  }, [forecast, present]);
  const floor = Math.min(0, ...(present.length ? present : [0]));

  const y = (v: number) => {
    const t = (v - floor) / (ceiling - floor || 1);
    return H - PAD_B - t * (H - PAD_T - PAD_B);
  };
  const yZero = y(0);

  // Colors (inline — SVG can't use the `day:` variant).
  const area = dayMode ? 'rgba(5,150,105,0.16)' : 'rgba(16,185,129,0.16)';
  const line = dayMode ? 'rgba(4,120,87,0.95)' : 'rgba(52,211,153,0.95)';
  const danger = dayMode ? 'rgba(220,38,38,0.16)' : 'rgba(239,68,68,0.18)';
  const zeroStroke = dayMode ? 'rgba(220,38,38,0.55)' : 'rgba(248,113,113,0.55)';
  const todayStroke = dayMode ? 'rgba(234,179,8,0.6)' : 'rgba(255,204,51,0.5)';
  const gridStroke = dayMode ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';

  // Stepped area path: each day a flat segment across its column, filled to the
  // SVG bottom. Gaps (no data) break the path so we don't draw through them.
  const areaPath = useMemo(() => {
    let d = '';
    let open = false;
    vals.forEach((v, i) => {
      const x0 = i * DAY_W;
      const x1 = (i + 1) * DAY_W;
      if (v == null) {
        if (open) { d += ` L ${x0} ${H} Z`; open = false; }
        return;
      }
      const yy = y(v);
      if (!open) { d += ` M ${x0} ${H} L ${x0} ${yy}`; open = true; }
      else { d += ` L ${x0} ${yy}`; }
      d += ` L ${x1} ${yy}`;
    });
    if (open) d += ` L ${vals.length * DAY_W} ${H} Z`;
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vals, ceiling, floor]);

  const sev = minAvail == null ? 'none'
    : minAvail <= 0 ? 'over'
    : (totalFleet > 0 && minAvail < totalFleet * 0.1) ? 'low'
    : 'ok';
  const sevText =
    sev === 'over' ? 'text-red-400 day:text-red-600'
    : sev === 'low' ? 'text-amber-400 day:text-amber-600'
    : sev === 'ok' ? 'text-emerald-400 day:text-emerald-600'
    : 'text-on-surface-variant day:text-gray-500';

  return (
    <div className="flex">
      {/* Sticky gutter — label + the tightest (min) availability across the window. */}
      <div
        className="sticky left-0 z-20 shrink-0 bg-gray-850 day:bg-gray-50 border-b border-r border-gray-700/60 day:border-gray-200 px-3 flex flex-col justify-center"
        style={{ width: INFO_W, height: H }}
      >
        <div className="text-[10px] uppercase tracking-wider text-on-surface-variant day:text-gray-500 font-medium">Available drones</div>
        <div className="flex items-baseline gap-1.5">
          {unavailable ? (
            <span className="text-xs font-semibold text-amber-500/90 day:text-amber-700">unavailable</span>
          ) : (
            <>
              <span className={`text-lg font-bold tabular-nums ${sevText}`}>{minAvail == null ? '—' : minAvail}</span>
              <span className="text-[10px] text-on-surface-variant day:text-gray-500">min over window{totalFleet ? ` · ${totalFleet} fleet` : ''}</span>
            </>
          )}
        </div>
      </div>

      {/* Plot — scrolls horizontally with the grid (same container). */}
      <div
        ref={plotRef}
        className="relative bg-gray-900 day:bg-white border-b border-gray-700/60 day:border-gray-200"
        style={{ width: totalWidth, height: H }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <svg width={totalWidth} height={H} className="block" preserveAspectRatio="none">
          {/* danger columns (available ≤ 0) */}
          {vals.map((v, i) => (v != null && v <= 0 ? (
            <rect key={`d-${i}`} x={i * DAY_W} y={0} width={DAY_W} height={H} fill={danger} />
          ) : null))}
          {/* top gridline */}
          <line x1={0} y1={PAD_T} x2={totalWidth} y2={PAD_T} stroke={gridStroke} strokeWidth={1} />
          {/* zero baseline (only meaningful if we dip to/under it) */}
          {floor < 0 && (
            <line x1={0} y1={yZero} x2={totalWidth} y2={yZero} stroke={zeroStroke} strokeWidth={1} strokeDasharray="4 3" />
          )}
          {/* available-drones stepped area + top line */}
          <path d={areaPath} fill={area} stroke="none" />
          <path d={areaPath} fill="none" stroke={line} strokeWidth={1.5} strokeLinejoin="round" />
          {/* today marker */}
          {todayIdx !== undefined && (
            <line x1={todayIdx * DAY_W + DAY_W / 2} y1={0} x2={todayIdx * DAY_W + DAY_W / 2} y2={H} stroke={todayStroke} strokeWidth={1} />
          )}
          {/* hover crosshair */}
          {hover && (
            <line x1={hover.idx * DAY_W + DAY_W / 2} y1={0} x2={hover.idx * DAY_W + DAY_W / 2} y2={H} stroke={dayMode ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.3)'} strokeWidth={1} />
          )}
        </svg>
        {unavailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-amber-500/[0.06] day:bg-amber-500/10 pointer-events-none">
            <span className="text-[11px] font-medium text-amber-500/90 day:text-amber-700">
              {forecastError ? 'Availability forecast unavailable — refresh to retry' : 'No availability data for this window'}
            </span>
          </div>
        )}
      </div>

      {/* Hover tooltip — leads with availability, then the leases/rentals that
          explain a dip with no visible event on the grid. Portaled so the scroll
          container's overflow can't clip it. */}
      {hover && (() => {
        const day = days[hover.idx];
        const p = pointByDate.get(day);
        if (!p) return null;
        // Centered on the cursor's column. Float above the strip when there's room,
        // else flip below it — the strip sits high on the page, so above would clip.
        const left = Math.max(8, Math.min(hover.x - 115, window.innerWidth - 238));
        const placeBelow = hover.top < 260;
        const top = placeBelow ? hover.top + H + 8 : hover.top - 8;
        const availColor = p.availableDrones <= 0
          ? 'text-red-400 day:text-red-600'
          : (p.totalFleet > 0 && p.availableDrones < p.totalFleet * 0.1) ? 'text-amber-400 day:text-amber-600' : 'text-emerald-400 day:text-emerald-600';
        return createPortal(
          <div
            className="fixed z-[1200] w-[230px] bg-gray-850 day:bg-white border border-gray-700 day:border-gray-200 rounded-md shadow-overlay day:shadow-xl day:shadow-gray-900/10 p-3 text-xs pointer-events-none"
            style={{ left, top, transform: placeBelow ? 'none' : 'translateY(-100%)' }}
          >
            <div className="font-semibold text-on-surface day:text-gray-900">{formatEventDate(day, 'ddd, MMM D')}</div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className={`text-base font-bold tabular-nums ${availColor}`}>{p.availableDrones}</span>
              <span className="text-[10px] text-on-surface-variant day:text-gray-500">available · {p.totalFleet} fleet</span>
            </div>

            {p.leases.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-700/50 day:border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-on-surface-variant day:text-gray-500 mb-0.5">Leased · {p.totalLeased}</div>
                {p.leases.slice(0, 5).map((l, i) => (
                  <div key={i} className="flex justify-between gap-2 text-on-surface day:text-gray-700">
                    <span className="truncate">{l.customer || 'Lease'}</span>
                    <span className="tabular-nums text-on-surface-variant day:text-gray-600">{l.ls + l.pyro}</span>
                  </div>
                ))}
                {p.leases.length > 5 && <div className="text-[10px] text-on-surface-variant day:text-gray-500">+{p.leases.length - 5} more</div>}
              </div>
            )}

            {p.rentals.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-700/50 day:border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-on-surface-variant day:text-gray-500 mb-0.5">Rentals · {p.committedByRentals}</div>
                {p.rentals.slice(0, 5).map((r, i) => (
                  <div key={i} className="flex justify-between gap-2 text-on-surface day:text-gray-700">
                    <span className="truncate">{r.name || r.so_number}</span>
                    <span className="tabular-nums text-on-surface-variant day:text-gray-600">{r.ls + r.pyro}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-2 pt-2 border-t border-gray-700/50 day:border-gray-200 flex justify-between text-on-surface-variant day:text-gray-600">
              <span>Shows committed</span><span className="tabular-nums">{p.committedByEvents}</span>
            </div>
            {p.leases.length === 0 && p.rentals.length === 0 && (
              <div className="mt-1 text-[10px] text-on-surface-variant day:text-gray-500">No leases or rentals this day.</div>
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default ForecastStrip;
